import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  Droplets,
  Wrench,
  Filter,
  Grid3X3,
  ArrowDownCircle,
  Sparkles,
  Shield,
  Clock,
  User,
  Heart,
  Star,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { useEffect } from "react";

const PHONE = "5599996208";
const PHONE_DISPLAY = "(559) 999-6208";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const stagger = {
  initial: "hidden",
  whileInView: "show",
  viewport: { once: true, margin: "-50px" },
  variants: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12 } },
  },
};

const childFade = {
  variants: {
    hidden: { opacity: 0, y: 25 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  },
};

const SERVICES = [
  {
    icon: Grid3X3,
    title: "Tile Cleaning",
    hash: "tile",
    star: true,
    desc: "Remove stubborn calcium buildup and restore your pool tile to its original beauty with professional cleaning.",
    accent: "from-gold/20 to-gold-light/10",
  },
  {
    icon: Droplets,
    title: "Pool Maintenance",
    hash: "maintenance",
    desc: "Keep your pool sparkling with professional chemical balancing, skimming, and thorough equipment checks.",
    accent: "from-pool-aqua/20 to-pool-light/10",
  },
  {
    icon: Wrench,
    title: "Equipment Repair",
    hash: "repair",
    desc: "Expert diagnosis and repair of pumps, heaters, filters, and all pool equipment to keep everything running smoothly.",
    accent: "from-gold/20 to-gold-light/10",
  },
  {
    icon: Filter,
    title: "Filter Cleaning",
    hash: "filter",
    desc: "Thorough deep cleaning of cartridge, sand, and DE filters for optimal water circulation and clarity.",
    accent: "from-pool-aqua/20 to-pool-light/10",
  },
  {
    icon: ArrowDownCircle,
    title: "Drain Clean-Ups",
    hash: "drain",
    desc: "Complete pool draining with acid or chlorine wash if needed, TDS/CYA testing, and fresh refill.",
    accent: "from-pool-aqua/20 to-pool-light/10",
  },
  {
    icon: Sparkles,
    title: "Green Pool Recovery",
    hash: "green-pool",
    desc: "Transform your green, algae-filled pool back to crystal clear water. Recovery available when CYA is at 30-50 ppm.",
    accent: "from-emerald-500/20 to-green-300/10",
  },
];

const TRUST_POINTS = [
  { icon: Shield, label: "A+ BBB Rating", desc: "Verified by the Better Business Bureau" },
  { icon: Clock, label: "15+ Years Experience", desc: "6 years as owner of his own business" },
  { icon: User, label: "Owner-Operated", desc: "Pete personally oversees every job" },
  { icon: Heart, label: "Reliable Service", desc: "Consistent, dependable professional care" },
  { icon: Star, label: "Professional Care", desc: "Friendly, skilled, community-focused" },
];

/* ─── Main Page ─── */
export default function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="overflow-hidden">
      {/* ═══ HERO ═══ */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        {/* BG Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/zzv3ezi3_Screenshot%202026-03-05%20at%207.24.12%E2%80%AFPM.png"
            alt="Luxury pool serviced by Pete's Pool Service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pool-deep/90 via-pool-deep/70 to-pool-deep/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-pool-deep/60 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 lg:pt-40 lg:pb-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left content */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-6">
                  Fresno & Clovis, California
                </p>
                <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-[0.95] tracking-tight mb-6">
                  Professional
                  <br />
                  <span className="text-pool-aqua">Pool Service</span>
                  <br />
                  You Can Trust
                </h1>
                <p className="text-white/70 text-lg md:text-xl max-w-lg leading-relaxed mb-10 font-body">
                  Reliable maintenance, expert repairs, and green-to-clean
                  restorations from a trusted local pool professional.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${PHONE}`}
                    data-testid="hero-call-now"
                    className="inline-flex items-center justify-center gap-3 bg-pool-aqua text-pool-deep px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-lg hover:shadow-pool-aqua/30 hover:-translate-y-1"
                  >
                    <Phone size={20} />
                    Call Now
                  </a>
                  <Link
                    to="/contact"
                    data-testid="hero-contact-us"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white/20 hover:-translate-y-1"
                  >
                    Contact Us
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Right - Trust Card */}
            <div className="lg:col-span-5 hidden lg:flex justify-end">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                className="animate-float"
              >
                <div className="glass-panel rounded-3xl p-8 max-w-sm shadow-2xl shadow-black/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                      <Shield size={22} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-pool-deep text-lg">Trusted Pro</p>
                      <p className="text-xs text-pool-deep/50 font-body">Verified credentials</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {[
                      { label: "A+ BBB Rating", sub: "Better Business Bureau" },
                      { label: "15+ Years Experience", sub: "6 years on his own" },
                      { label: "Owner Operated", sub: "Pete Cervantes" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-pool-aqua/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="#00B4D8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-pool-deep">{item.label}</p>
                          <p className="text-xs text-pool-deep/50">{item.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-200/50">
                    <p className="text-xs font-accent italic text-pool-deep/50 text-center">
                      Se Habla Español
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path
              d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,100 L0,100 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16 md:mb-20">
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              What We Do
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-pool-deep tracking-tight">
              Our Services
            </h2>
            <p className="mt-4 text-slate-500 text-base md:text-lg max-w-2xl mx-auto">
              From routine maintenance to emergency green pool recovery, we keep your pool in perfect condition.
            </p>
          </motion.div>

          <motion.div
            {...stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {SERVICES.map((service) => {
              const Icon = service.icon;
              return (
                <motion.div key={service.title} {...childFade}>
                  <Link
                    to={`/services#${service.hash}`}
                    data-testid={`service-card-${service.title.toLowerCase().replace(/\s/g, '-')}`}
                    className="group block relative overflow-hidden bg-white border border-slate-100 rounded-2xl p-8 hover:shadow-2xl hover:shadow-pool-aqua/10 transition-all duration-500 hover:-translate-y-1"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-2xl bg-pool-aqua/10 flex items-center justify-center mb-6 group-hover:bg-pool-aqua/20 transition-colors duration-300">
                        <Icon size={26} className="text-pool-aqua" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-pool-deep mb-3 flex items-center gap-2">
                        {service.title}
                        {service.star && <Star size={16} className="text-gold fill-gold" />}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {service.desc}
                      </p>
                      <div className="mt-6 flex items-center gap-2 text-pool-aqua text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Learn More <ArrowRight size={14} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══ GALLERY ═══ */}
      <section data-testid="gallery-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              Our Work
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-pool-deep tracking-tight">
              Pools We Maintain
            </h2>
          </motion.div>
          <motion.div
            {...stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/p7u60kcc_Screenshot%202026-03-05%20at%207.23.05%E2%80%AFPM.png",
                alt: "Pool tile work and stone coping maintained by Pete's Pool Service",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/wk257s3w_Screenshot%202026-03-05%20at%207.23.48%E2%80%AFPM.png",
                alt: "Large backyard pool maintained by Pete's Pool Service in Fresno",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/zzv3ezi3_Screenshot%202026-03-05%20at%207.24.12%E2%80%AFPM.png",
                alt: "Luxury pool with mosaic tile cleaned by Pete's Pool Service",
              },
            ].map((img, i) => (
              <motion.div key={i} {...childFade}>
                <div className="group relative overflow-hidden rounded-2xl aspect-square shadow-lg">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-pool-deep/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ WHY CHOOSE PETE ═══ */}
      <section data-testid="trust-section" className="py-24 md:py-32 bg-pool-deep relative overflow-hidden">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(0,180,216,0.3) 0%, transparent 50%),
                              radial-gradient(circle at 75% 75%, rgba(212,175,55,0.2) 0%, transparent 50%)`
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16 md:mb-20">
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              Why Pete's Pool
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight">
              Your Pool Deserves the Best
            </h2>
          </motion.div>

          <motion.div
            {...stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6"
          >
            {TRUST_POINTS.map((point) => {
              const Icon = point.icon;
              return (
                <motion.div
                  key={point.label}
                  {...childFade}
                  className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 rounded-2xl bg-pool-aqua/15 flex items-center justify-center mx-auto mb-4">
                    <Icon size={24} className="text-pool-aqua" />
                  </div>
                  <h3 className="text-white font-bold text-sm mb-1">{point.label}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{point.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══ SERVICE AREAS ═══ */}
      <section data-testid="service-areas-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeUp}>
              <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
                Service Areas
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-pool-deep tracking-tight mb-6">
                Proudly Serving
                <br />
                <span className="text-pool-aqua">Fresno & Clovis</span>
              </h2>
              <p className="text-slate-500 text-base md:text-lg leading-relaxed mb-8">
                We provide professional pool maintenance and repair throughout
                Fresno County, including all neighborhoods in Fresno and Clovis, California.
              </p>
              <div className="space-y-3 mb-8">
                {["Fresno, CA", "Clovis, CA", "Fresno County"].map((area) => (
                  <div key={area} className="flex items-center gap-3">
                    <MapPin size={16} className="text-pool-aqua flex-shrink-0" />
                    <span className="text-pool-deep font-medium">{area}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="service-areas-cta"
                className="inline-flex items-center gap-2 bg-pool-deep text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:bg-pool-aqua hover:shadow-lg hover:shadow-pool-aqua/25 hover:-translate-y-1"
              >
                Check If We Service Your Area
                <ArrowRight size={18} />
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.2 }}>
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-pool-deep/10 border border-slate-100">
                <iframe
                  title="Pete's Pool Service Area - Fresno & Clovis"
                  src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d103000!2d-119.82!3d36.79!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sFresno+and+Clovis+CA!5e0!3m2!1sen!2sus!4v1709669000000!5m2!1sen!2sus"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full"
                  data-testid="google-maps-embed"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ CTA BANNER ═══ */}
      <section data-testid="cta-banner" className="py-20 md:py-28 bg-gradient-to-r from-pool-aqua via-cyan-500 to-pool-aqua relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready for a Cleaner Pool?
            </h2>
            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Call Pete today for a free consultation. We'll get your pool looking
              its best in no time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`tel:${PHONE}`}
                data-testid="cta-call-now"
                className="inline-flex items-center gap-3 bg-white text-pool-deep px-10 py-5 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-xl hover:shadow-white/25 hover:-translate-y-1"
              >
                <Phone size={22} />
                {PHONE_DISPLAY}
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact-us"
                className="inline-flex items-center gap-2 bg-pool-deep text-white px-10 py-5 rounded-full text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                Contact Us
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Pete's Pool Service & Repair",
            description: "Professional pool cleaning services in Fresno and Clovis, California. Pool cleaning Fresno, pool services Clovis, tile cleaning, equipment repair, filter cleaning, drain clean-ups, and green pool recovery. A+ BBB rated, 15+ years experience.",
            telephone: "+1-559-999-6208",
            areaServed: [
              { "@type": "City", name: "Fresno", addressRegion: "CA" },
              { "@type": "City", name: "Clovis", addressRegion: "CA" },
            ],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Fresno",
              addressRegion: "CA",
              addressCountry: "US",
            },
            founder: { "@type": "Person", name: "Pete Cervantes" },
            foundingDate: "2019-03-31",
            knowsLanguage: ["English", "Spanish"],
            priceRange: "$$",
            openingHours: "Mo-Sa 07:00-18:00",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5",
              bestRating: "5",
              ratingCount: "1",
            },
          }),
        }}
      />
    </div>
  );
}
